// v50.212 — Sidebar (nav redesign Phase 1).
// v50.227 — Apple-style expandable areas. Each area with secondary
// items now carries a disclosure chevron; clicking the chevron toggles
// the submenu independently of navigation. Active area still
// auto-expands so the current section is always disclosed. Manual
// toggles persist across navigations until toggled back.
// v50.228 — Bug fix: clicking the chevron on an area whose `secondary`
// items include the current pathname (e.g. Projects, which lists
// /requirements, /tenders, /deals, /futures) had no effect because
// `isActive` forced the panel open regardless of the user's toggle.
// Replaced the additive Set with a Map<string, boolean> so the
// chevron can hard-override the auto-expand in either direction.
//
// Renders the role-aware 8-area primary rail + the grouped secondary
// vertical sub-nav defined in navAreas.ts. Replaces the flat 14-item
// rail driven by the old nav.ts NAV_ITEMS.
//
// Behaviour summary (see Build notes/labourcore-nav-redesign-brief.md):
//   - Universal areas (Today, Inbox, Compliance, Payments, Insights,
//     Settings) never move; only positions 3-4 swap on View as.
//   - Active area: longest-prefix match against pathname, including
//     legacy routes and any secondary[].route.
//   - Secondary panel renders when the area is active OR manually
//     expanded via the disclosure chevron. Items are grouped by
//     `group`; items with `status: 'new'` render disabled with a
//     small "Soon" tag and don't navigate.
//   - Notifications is de-listed from the rail per §6 (still reachable
//     at /notifications and via the topbar bell in a follow-up ship).

import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useShellUser } from "@/features/auth/AuthContext";
import { adminTierOf } from "@/features/admin/components/AdminGate";
import { useEffectiveRole, useRoleOverride, type RoleOverride } from "../roleOverride";
import { useSidebarBadges } from "../useSidebarBadges";
import { ProfileMenu } from "./ProfileMenu";
import { APP_VERSION } from "@/lib/version";
import { Icon } from "@/components/Icon";
import {
  railFor, activeAreaFor, groupSecondary, ADMIN_NAV,
  type NavArea, type SecondaryItem,
} from "../navAreas";

interface SidebarProps {
  onOpenSearch?: () => void;
}

export function Sidebar({ onOpenSearch }: SidebarProps = {}) {
  const user           = useShellUser();
  const navigate       = useNavigate();
  const location       = useLocation();
  const effectiveRole  = useEffectiveRole();
  const badges         = useSidebarBadges();

  const areas       = railFor(effectiveRole);
  const activeArea  = activeAreaFor(location.pathname);

  /* v50.228 — user-toggled expansion overrides. Map<areaId, expanded?>.
     Absent → fall back to the auto-expand-on-active default.
     Present → hard override (even if the area is currently active).
     This lets the chevron close an active area whose secondary list
     happens to cover the current route (Projects's secondary covers
     /requirements, /tenders, /deals, /futures, etc — without the
     override the panel would stay forced open). */
  const [userExpanded, setUserExpanded] = useState<Map<string, boolean>>(() => new Map());

  /* v50.322 — admin panel disclosure. null → auto (open on /admin
     routes, closed elsewhere); true/false → user override. */
  const [adminOverride, setAdminOverride] = useState<boolean | null>(null);
  const toggleExpanded = (id: string, currentlyExpanded: boolean) => {
    setUserExpanded(prev => {
      const next = new Map(prev);
      next.set(id, !currentlyExpanded);
      return next;
    });
  };

  /* Badge counts. The new IA assigns these to the area-level destination. */
  const badgeForArea = (area: NavArea): number => {
    if (area.id === "inbox")    return badges.inbox;
    if (area.id === "find-work") return badges.tenders;
    if (area.id === "projects") return badges.futures;
    return 0;
  };

  /* Mac vs other-OS shortcut hint — keyboard shortcut works on both. */
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform);

  /* When a secondary item carries query params we navigate
     programmatically (NavLink would also work but its activeness check
     fights with the search string). */
  const goSecondary = (item: SecondaryItem) => {
    if (item.status === "new") return;
    navigate(item.route);
  };

  /* Active match for secondary items — checks the pathname + the
     query-string component if the item uses one. */
  const isSecondaryActive = (item: SecondaryItem): boolean => {
    const [path, qs] = item.route.split("?");
    if (location.pathname !== path && !location.pathname.startsWith(path + "/")) return false;
    if (!qs) return true;
    /* When the route declares ?tab=foo (or similar), require it to match. */
    const wantParams = new URLSearchParams(qs);
    const haveParams = new URLSearchParams(location.search);
    for (const [k, v] of wantParams.entries()) {
      if (haveParams.get(k) !== v) return false;
    }
    return true;
  };

  return (
    <aside className="w-60 shrink-0 bg-white border-r border-lc-line h-screen sticky top-0 flex flex-col">
      <div className="px-5 py-5 flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-lc-blue text-white font-extrabold text-sm flex items-center justify-center">LC</div>
        <div className="font-extrabold text-lc-ink tracking-tight">LabourCore</div>
      </div>

      {/* Search trigger — opens the Cmd-K palette. */}
      {onOpenSearch && (
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={onOpenSearch}
            aria-label="Search the platform"
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lc-md bg-lc-bg-2 hover:bg-lc-bg-2/70 border border-lc-line text-lc-mute hover:text-lc-ink-2 text-[13px] font-semibold transition-colors"
          >
            <Icon name="search" size={14} />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="px-1.5 py-0.5 rounded-lc-sm border border-lc-line bg-white text-[10px] font-extrabold text-lc-mute leading-none">
              {isMac ? "⌘K" : "Ctrl K"}
            </kbd>
          </button>
        </div>
      )}

      <nav
        aria-label="Primary navigation"
        className="flex-1 px-2 py-1 flex flex-col gap-0.5 overflow-y-auto"
      >
        {areas.map(area => {
          const isActive    = activeArea?.id === area.id;
          const count       = badgeForArea(area);
          const hasSecondary = !!(area.secondary && area.secondary.length > 0);
          /* Auto-expand on active by default; user override wins if set. */
          const override     = userExpanded.get(area.id);
          const isExpanded   = hasSecondary && (override !== undefined ? override : isActive);
          const panelId      = `area-secondary-${area.id}`;
          /* Top-level area: use the canonical brief path. App.tsx
             redirects /today, /find-labour, /find-work, /my-crew to the
             existing pages. */
          return (
            <div key={area.id} className="flex flex-col">
              <div
                className={`flex items-stretch rounded-lc-md transition-colors ${
                  isActive
                    ? "bg-lc-blue-light"
                    : "hover:bg-lc-bg-2"
                }`}
              >
                <NavLink
                  to={area.route}
                  className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lc-md text-sm font-semibold transition-colors ${
                    isActive ? "text-lc-blue-dark" : "text-lc-ink-2"
                  }`}
                >
                  <span className={isActive ? "text-lc-blue-dark" : "text-lc-mute"}>
                    <Icon name={area.icon} size={16} />
                  </span>
                  <span className="flex-1 min-w-0 truncate">{area.label}</span>
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-extrabold leading-none ${
                      isActive ? "bg-lc-blue-dark text-white" : "bg-lc-blue text-white"
                    }`}>
                      {count > 99 ? "99+" : count}
                    </span>
                  )}
                </NavLink>
                {hasSecondary && (
                  <button
                    type="button"
                    onClick={() => toggleExpanded(area.id, isExpanded)}
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${area.label} submenu`}
                    className={`shrink-0 px-2 flex items-center justify-center rounded-r-lc-md transition-colors ${
                      isActive ? "text-lc-blue-dark hover:bg-lc-blue-light/70" : "text-lc-mute hover:text-lc-ink-2"
                    }`}
                  >
                    <Icon
                      name="chevron-down"
                      size={12}
                      className={`transition-transform duration-150 ${isExpanded ? "rotate-0" : "-rotate-90"}`}
                    />
                  </button>
                )}
              </div>

              {/* Grouped secondary panel — renders when area is active
                  OR manually expanded via the chevron. */}
              {isExpanded && area.secondary && (
                <div id={panelId} className="ml-2 mt-1 mb-2 flex flex-col gap-2 border-l-2 border-lc-line pl-2">
                  {groupSecondary(area.secondary).map(({ group, items }) => (
                    <div key={group || "_ungrouped"} className="flex flex-col gap-0.5">
                      {group && (
                        <div className="lc-eyebrow px-2 mt-1 mb-0.5">{group}</div>
                      )}
                      {items.map(item => {
                        const subActive = isSecondaryActive(item);
                        const isSoon    = item.status === "new";
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => goSecondary(item)}
                            disabled={isSoon}
                            className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lc-sm text-[12px] text-left transition-colors ${
                              isSoon
                                ? "text-lc-faint cursor-not-allowed"
                                : subActive
                                  ? "bg-lc-blue-light text-lc-blue-dark font-extrabold"
                                  : "text-lc-mute font-semibold hover:bg-lc-bg-2 hover:text-lc-ink"
                            }`}
                          >
                            <span className={`shrink-0 inline-block w-1 h-1 rounded-full ${subActive ? "bg-lc-blue" : "bg-transparent"}`} />
                            <span className="flex-1 min-w-0 truncate">{item.label}</span>
                            {isSoon && (
                              <span className="text-[9px] font-extrabold uppercase tracking-widest text-lc-faint border border-lc-line rounded-full px-1.5 py-0.5">
                                Soon
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* v49.24 — hybrid view-as switcher (kept in footer per brief §4). */}
      {user?.role === "hybrid" && <HybridRoleSwitch />}

      {/* v49.69 / v49.77 / v49.79 / v50.321 — admin back-office links,
          rendered per platform-admin tier (master / board / investor).
          v50.322 — collapsible: the full link list (8 entries for master)
          was crowding the primary nav into a scroll. Auto-expands on
          /admin routes; the header chevron hard-overrides either way.
          v50.323 — restyled to match the primary nav areas: area-style
          header row (icon + soft active highlight + split chevron) and
          the links rendered like the grouped secondary panel. */}
      {adminTierOf(user) && (() => {
        const adminNav      = ADMIN_NAV[adminTierOf(user)!];
        const onAdminRoute  = location.pathname.startsWith("/admin");
        const adminExpanded = adminOverride !== null ? adminOverride : onAdminRoute;
        return (
          <div className="border-t border-lc-line px-2 py-2">
            <div
              className={`flex items-stretch rounded-lc-md transition-colors ${
                onAdminRoute ? "bg-lc-blue-light" : "hover:bg-lc-bg-2"
              }`}
            >
              <NavLink
                to={adminNav.links[0].to}
                className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lc-md text-sm font-semibold transition-colors ${
                  onAdminRoute ? "text-lc-blue-dark" : "text-lc-ink-2"
                }`}
              >
                <span className={onAdminRoute ? "text-lc-blue-dark" : "text-lc-mute"}>
                  <Icon name="shield" size={16} />
                </span>
                <span className="flex-1 min-w-0 truncate">{adminNav.label}</span>
              </NavLink>
              <button
                type="button"
                onClick={() => setAdminOverride(!adminExpanded)}
                aria-expanded={adminExpanded}
                aria-controls="admin-nav-panel"
                aria-label={`${adminExpanded ? "Collapse" : "Expand"} ${adminNav.label} submenu`}
                className={`shrink-0 px-2 flex items-center justify-center rounded-r-lc-md transition-colors ${
                  onAdminRoute ? "text-lc-blue-dark hover:bg-lc-blue-light/70" : "text-lc-mute hover:text-lc-ink-2"
                }`}
              >
                <Icon
                  name="chevron-down"
                  size={12}
                  className={`transition-transform duration-150 ${adminExpanded ? "rotate-0" : "-rotate-90"}`}
                />
              </button>
            </div>
            {adminExpanded && (
              <div id="admin-nav-panel" className="ml-2 mt-1 mb-1 flex flex-col gap-0.5 border-l-2 border-lc-line pl-2">
                {adminNav.links.map(l => (
                  <NavLink key={l.to} to={l.to} end={l.to === "/admin"}
                    className={({ isActive }) =>
                      `flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lc-sm text-[12px] transition-colors ${
                        isActive
                          ? "bg-lc-blue-light text-lc-blue-dark font-extrabold"
                          : "text-lc-mute font-semibold hover:bg-lc-bg-2 hover:text-lc-ink"
                      }`
                    }>
                    {({ isActive }) => (
                      <>
                        <span className={`shrink-0 inline-block w-1 h-1 rounded-full ${isActive ? "bg-lc-blue" : "bg-transparent"}`} />
                        <span className="flex-1 min-w-0 truncate">{l.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Profile + sign-out — only renders when there's a user. */}
      {user && (
        <div className="border-t border-lc-line px-3 py-3">
          <ProfileMenu />
        </div>
      )}

      <div className="px-5 py-3 border-t border-lc-line">
        <div className="lc-eyebrow">{APP_VERSION}</div>
      </div>
    </aside>
  );
}

function HybridRoleSwitch() {
  const [override, setOverride] = useRoleOverride();
  const opts: { value: RoleOverride; label: string }[] = [
    { value: null,          label: "Both" },
    { value: "coordinator", label: "Coord" },
    { value: "partner",     label: "Partner" },
  ];
  return (
    <div className="border-t border-lc-line px-3 py-3">
      <div className="lc-eyebrow mb-1.5 px-1">View as</div>
      <div className="grid grid-cols-3 gap-1 bg-lc-bg-2 rounded-lc-md p-0.5">
        {opts.map(o => {
          const active = override === o.value;
          return (
            <button
              key={o.label}
              type="button"
              onClick={() => setOverride(o.value)}
              className={`px-2 py-1.5 rounded-lc-sm text-[11px] font-extrabold transition-colors ${
                active ? "bg-white text-lc-blue shadow-sm" : "text-lc-mute hover:text-lc-ink"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
